/**
 * 主线程封装：与 public/wasm/codec2/codec2DecodeWorker.js 通信（与 c2dec 同路径）。
 */

import { getCodec2Mode } from '../../constants/Codec2Constants';

const DEFAULT_TIMEOUT_MS = 15000;

function workerScriptUrl(): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const path = `${base}/wasm/codec2/codec2DecodeWorker.js`;
  if (typeof window === 'undefined') {
    return path;
  }
  return `${window.location.origin}${path}`;
}

export type Codec2DecodeResult =
  | { ok: true; pcm: ArrayBuffer }
  | { ok: false; error: string };

export class Codec2DecodeClient {
  private worker: Worker | null = null;
  private nextId = 1;
  private pending = new Map<
    number,
    { resolve: (v: Codec2DecodeResult) => void; reject: (e: Error) => void; timer: number }
  >();

  private ensureWorker(): Worker {
    if (this.worker) {
      return this.worker;
    }
    const url = workerScriptUrl();
    this.worker = new Worker(url);
    this.worker.onmessage = (ev: MessageEvent) => {
      const data = ev.data;
      if (!data || (data.type !== 'decoded' && data.type !== 'decodedMany')) {
        return;
      }
      const id = data.id as number;
      const slot = this.pending.get(id);
      if (!slot) {
        return;
      }
      window.clearTimeout(slot.timer);
      this.pending.delete(id);
      if (data.ok) {
        const pcm = data.payload as ArrayBuffer | undefined;
        if (!pcm || pcm.byteLength === 0) {
          slot.resolve({ ok: false, error: 'Codec2 decode returned empty pcm' });
        } else {
          slot.resolve({ ok: true, pcm });
        }
      } else {
        slot.resolve({ ok: false, error: String(data.error || 'decode failed') });
      }
    };
    this.worker.onerror = (e) => {
      const errMsg = e.message || 'Codec2 decode worker error';
      this.pending.forEach((slot) => {
        window.clearTimeout(slot.timer);
        slot.resolve({ ok: false, error: errMsg });
      });
      this.pending.clear();
    };
    return this.worker;
  }

  /**
   * @param mode Codec2 模式，须与上行/后端一致
   * @param bit 单帧压缩比特（如 mode 3200 常见 8 字节）
   */
  decode(mode: string | undefined, bit: ArrayBuffer): Promise<Codec2DecodeResult> {
    const worker = this.ensureWorker();
    const resolvedMode = mode && mode.length > 0 ? mode : getCodec2Mode();
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        resolve({ ok: false, error: `Codec2 decode timeout (${DEFAULT_TIMEOUT_MS}ms)` });
      }, DEFAULT_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      try {
        worker.postMessage({ type: 'decode', id, mode: resolvedMode, bit }, [bit]);
      } catch (e) {
        window.clearTimeout(timer);
        this.pending.delete(id);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  decodeMany(mode: string | undefined, bits: ArrayBuffer[], frameBytes: number): Promise<Codec2DecodeResult> {
    const worker = this.ensureWorker();
    const resolvedMode = mode && mode.length > 0 ? mode : getCodec2Mode();
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        resolve({ ok: false, error: `Codec2 decodeMany timeout (${DEFAULT_TIMEOUT_MS}ms)` });
      }, DEFAULT_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      try {
        worker.postMessage(
          { type: 'decodeMany', id, mode: resolvedMode, bits, frameBytes },
          bits
        );
      } catch (e) {
        window.clearTimeout(timer);
        this.pending.delete(id);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pending.forEach((slot) => {
      window.clearTimeout(slot.timer);
      slot.resolve({ ok: false, error: 'worker terminated' });
    });
    this.pending.clear();
  }
}

let singleton: Codec2DecodeClient | null = null;

export function getCodec2DecodeClient(): Codec2DecodeClient {
  if (!singleton) {
    singleton = new Codec2DecodeClient();
  }
  return singleton;
}

export function disposeCodec2DecodeClient(): void {
  singleton?.terminate();
  singleton = null;
}
